import React, { createRef } from "react";
import { eatClick, resizeImage, _attr, LOG, isBlank } from "Util/Utilities";
import { UNDEF, API_UPLOAD } from "../../Constants";
import Api, { showSnackbar } from "../../Api";

export interface UploadResp {
  url_to_upload: string;
  url: string;
}

export const IMG_TYPE_CR_AVATAR = "cr_avatar";
export const IMG_TYPE_CO_AVATAR = "co_avatar";
export const IMG_TYPE_DOC_MEMO = "doctor_memo";
export const IMG_TYPE_HOME_ENV = "home_env_photo";
export const IMG_TYPE_MEDICATION_PHOTOS = "medication_photos";
export const UPLOAD_TYPE_MT_VIDEO = "mt_video";

export const IMG_TYPES = [
  IMG_TYPE_CR_AVATAR,
  IMG_TYPE_CO_AVATAR,
  IMG_TYPE_DOC_MEMO,
  IMG_TYPE_HOME_ENV,
  IMG_TYPE_MEDICATION_PHOTOS,
];

type FileUploadProps = {
  type:
    | "cr_avatar"
    | "co_avatar"
    | "doctor_memo"
    | "home_env_photo"
    | "mt_video"
    | "payment_proof"
    | "medication_photos";
  className?: string;
  acceptType?: string;
  hasError?: boolean;
  coId?: string;
  multiSelect?: boolean;

  onDragChange?: (isDragging: boolean) => void;
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (fileName: string) => void;
  onSuccess?: (fileName: string, s3Url: string, fileUrl: string) => void;
};

type FileUploadState = {
  _isDragging?: boolean;
  _isUploading?: boolean;
};

/*export function haveAdvanceFeatures() {
  const div = document.createElement("div");
  return (
    ("draggable" in div || ("ondragstart" in div && "ondrop" in div)) &&
    "FormData" in window &&
    "FileReader" in window
  );
}*/

export default class FileUpload extends React.Component<
  FileUploadProps,
  FileUploadState
> {
  //_haveDragNDrop = haveAdvanceFeatures();
  _bOnDragIn?: any;
  _bOnDragOut?: any;
  _bOnDrag?: any;
  _bOnDrop?: any;

  _urlToUpload?: string;
  _s3Url?: string;

  _inpRef = createRef<HTMLInputElement>();

  _files: any[] = [];

  constructor(props: FileUploadProps) {
    super(props);
    this.state = {};
  }

  _enableListeners(isEnabled: boolean) {
    const self = this,
      elem: any = self._inpRef.current;
    const listener = isEnabled ? "addEventListener" : "removeEventListener";

    if (isEnabled) {
      self._bOnDragIn = self._onDragIn.bind(self);
      self._bOnDragOut = self._onDragOut.bind(self);
      self._bOnDrag = self._onDrag.bind(self);
      self._bOnDrop = self._onDrop.bind(self);
    }

    elem[listener]("dragenter", self._bOnDragIn);
    elem[listener]("dragleave", self._bOnDragOut);
    elem[listener]("dragover", self._bOnDrag);
    elem[listener]("drop", self._bOnDrop);

    // clear the bindings to avoid memory leak
    if (!isEnabled) {
      self._bOnDragIn = self._bOnDragOut = self._bOnDrag = self._bOnDrop = UNDEF;
    }
  }

  componentDidMount() {
    this._enableListeners(true);
  }

  componentWillUnmount() {
    this._enableListeners(false);
  }

  _setDragState(isDragging: boolean) {
    const self = this,
      onDragchange = self.props.onDragChange;
    self.setState({
      _isDragging: isDragging,
    });
    onDragchange && onDragchange(isDragging);
  }

  _onDragIn(e: React.DragEvent<HTMLDivElement>) {
    eatClick(e);
    if (this.state._isUploading) return;
    this._setDragState(true);
  }
  _onDragOut(e: React.DragEvent<HTMLDivElement>) {
    eatClick(e);
    this._setDragState(false);
  }
  _onDrag(e: React.DragEvent<HTMLDivElement>) {
    eatClick(e);
  }
  _onDrop(e: React.DragEvent<HTMLDivElement>) {
    eatClick(e);

    //console.log('_onDrop')
    const self = this;
    self._setDragState(false);
    if (self.state._isUploading) return;

    const dt = e.dataTransfer;
    const files = dt && dt.files;
    self._processFiles(files);
    dt.clearData();
  }

  _onFileSelect = (e: any) => {
    this._processFiles(e.currentTarget.files);
  };

  _processFiles(files?: FileList) {
    const self = this;
    const props = self.props;
    if (!files || !files[0]) return;

    const length = props.multiSelect ? files.length : 1;
    const reqType = props.type;
    const list: any = [];

    for (let k = 0; k < length; k++) {
      const file = files[k];
      if (!file) continue;

      const fileType = (file.type || "").toLowerCase();

      if (IMG_TYPES.indexOf(reqType) >= 0 && fileType.indexOf("image") === -1) {
        showSnackbar("Only image files are allowed", "error", 3000);
        return;
      }

      if (
        reqType === UPLOAD_TYPE_MT_VIDEO &&
        fileType.indexOf("video") === -1
      ) {
        showSnackbar("Only video files are allowed", "error", 3000);
        return;
      }

      // max file size allowed 300mb
      if (file.size > 300 * 1048576) {
        showSnackbar(
          `File "${file.name}" too big. Maximum 300MB allowed`,
          "error",
          3000
        );
        return;
      }

      list.push(file);
    }

    self._files = list.reverse();
    if (!isBlank(list)) {
      self.setState({ _isUploading: true });
      props.onStart && props.onStart();
      self._readFile();
    }
  }

  _readFile() {
    const self = this;
    const props = self.props;
    const type = props.type;

    const file = self._files.pop();
    if (!file) {
      self.setState({ _isUploading: false });
      props.onEnd && props.onEnd();
      return;
    }

    const isAvatar = type === IMG_TYPE_CO_AVATAR || type === IMG_TYPE_CR_AVATAR;
    const isImage = isAvatar || file.type.indexOf("image") >= 0;

    if (isImage) {
      const reader = new FileReader();
      reader.readAsBinaryString(file);
      reader.onloadend = function () {
        const data = reader.result;
        // resize image
        resizeImage(
          data,
          (resizedData: any) => {
            resizedData && self._beginUpload(file, "image/jpeg", resizedData);
          },
          isAvatar ? 300 : 900
        );
      };
    } else {
      // video or other types
      self._beginUpload(file, file.type, file);
    }
  }

  _beginUpload(file: any, fileType: string, fileData: any) {
    if (!file || !fileData) return;

    //console.log('_beginUpload', file.name);
    const self = this;
    const props = self.props;
    const uploadType = props.type;

    const params = {
      content_type: fileType,
      type: uploadType,
      co_id: props.coId,
    };

    Api.POST(
      API_UPLOAD,
      params,
      (resp) => {
        const data = resp && resp.data && (resp.data.data as UploadResp);
        if (!data) {
          self._files = [];
          self.setState({ _isUploading: false });
          props.onError && props.onError(file.name);
          return;
        }
        LOG("API_UPLOAD_POST", data);
        self._uploadFile(
          data.url_to_upload,
          data.url,
          fileData,
          file,
          fileType
        );
      },
      false,
      true,
      false
    );
  }

  // Upload to s3 url
  _uploadFile(
    urlToUpload: string,
    s3Url: string,
    fileData: any,
    file: any,
    fileType: string
  ) {
    const self = this;
    const props = self.props;
    let apiPath = urlToUpload;

    const headers = { "Content-Type": fileType };
    Api.PUT(
      apiPath,
      fileData,
      (resp) => {
        LOG("API_UPLOAD_PUT", resp);
        if (resp && resp.status === 200) {
          const data = URL.createObjectURL(file);
          props.onSuccess && props.onSuccess(file.name, s3Url, data);

          self._readFile();
        } else {
          self._files = [];
          self.setState({ _isUploading: false });
          props.onError && props.onError(file.name);
        }
      },
      false,
      true,
      false,
      headers
    );
  }

  render() {
    const self = this,
      state = self.state,
      props = self.props;
    const isUploading = state._isUploading;

    return (
      <div
        className={props.className}
        style={s_styleMain}
        has-error={_attr(props.hasError)}
      >
        {props.children}
        <input
          ref={self._inpRef}
          style={s_file}
          type="file"
          multiple={props.multiSelect}
          accept={props.acceptType}
          disabled={isUploading}
          onChange={self._onFileSelect}
        />
      </div>
    );
  }
}

const s_styleMain: any = {
  position: "relative",
};

const s_file: any = {
  position: "absolute",
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  userSelect: "none",
  outline: "none",
  cursor: "pointer",
};
